import { useEffect, useState, useRef, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { RoutesApi } from '@/shared/api/routes'

export interface UseForestRoadLayerProps {
  isEnabled: boolean
  getAccessToken: () => Promise<string | null>
  /** Called when forest-roads request fails (e.g. area too large). */
  onError?: (message: string | null) => void
}

/**
 * Hook for managing forest road layer rendering
 * Displays forest roads when dead end discovery mode is enabled
 */
export function useForestRoadLayer({
  isEnabled,
  getAccessToken,
  onError
}: UseForestRoadLayerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const [forestRoads, setForestRoads] = useState<any[]>([])
  const isLoadingRef = useRef(false)
  const routesApiRef = useRef<RoutesApi | null>(null)
  const boundsRef = useRef<mapboxgl.LngLatBounds | null>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const eventListenersAttachedRef = useRef(false)
  const initializedRef = useRef(false)

  // Initialize API client
  useEffect(() => {
    if (!routesApiRef.current) {
      routesApiRef.current = new RoutesApi(getAccessToken)
    }
  }, [getAccessToken])

  // Max bbox area (sq deg) for forest-roads API - backend rejects larger (~0.05 = ~500 km²)
  const MAX_BBOX_AREA = 0.05
  const MAX_SIDE_DEG = Math.sqrt(MAX_BBOX_AREA) // ~0.224°

  /** Clip bbox to max area; returns center portion if bounds are too large */
  const clipBboxToMaxArea = useCallback((bbox: [number, number, number, number]): [number, number, number, number] => {
    const [minLng, minLat, maxLng, maxLat] = bbox
    const lngSpan = maxLng - minLng
    const latSpan = maxLat - minLat
    const area = lngSpan * latSpan
    if (area <= MAX_BBOX_AREA) return bbox
    const centerLng = (minLng + maxLng) / 2
    const centerLat = (minLat + maxLat) / 2
    const half = MAX_SIDE_DEG / 2
    return [
      centerLng - half,
      centerLat - half,
      centerLng + half,
      centerLat + half
    ]
  }, [])

  // Fetch forest roads for visible map bounds with progressive loading
  const fetchForestRoads = useCallback(async (bounds: mapboxgl.LngLatBounds, isInitial = false) => {
    if (!routesApiRef.current) {
      return
    }

    if (isLoadingRef.current && !isInitial) {
      return
    }

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    
    const rawBbox: [number, number, number, number] = [
      sw.lng,
      sw.lat,
      ne.lng,
      ne.lat
    ]
    const fullBbox = clipBboxToMaxArea(rawBbox)

    // For initial load, fetch a smaller area first (1km radius) for faster response
    if (isInitial) {
      const center = bounds.getCenter()
      const radius = 0.009 // ~1km in degrees
      const quickBbox: [number, number, number, number] = [
        center.lng - radius,
        center.lat - radius,
        center.lng + radius,
        center.lat + radius
      ]

      isLoadingRef.current = true
      
      const clearErr = () => onError?.(null)
      const quickFetch = routesApiRef.current.getForestRoadsForBounds(quickBbox)
        .then((quickResponse) => {
          clearErr()
          if (quickResponse && quickResponse.success && quickResponse.features) {
            setForestRoads(quickResponse.features)
          }
        })
        .catch((err: any) => {
          const msg = err?.response?.data?.error
          const code = err?.response?.data?.code
          if (err?.response?.status === 400 && (code === 'BBOX_TOO_LARGE' || (typeof msg === 'string' && msg.toLowerCase().includes('zoom in')))) {
            onError?.('Zoom in to load forest roads.')
          }
        })

      const fullFetch = routesApiRef.current.getForestRoadsForBounds(fullBbox)
        .then((response) => {
          clearErr()
          if (response && response.success && response.features) {
            setForestRoads(response.features)
          }
        })
        .catch((err: any) => {
          const msg = err?.response?.data?.error
          const code = err?.response?.data?.code
          if (err?.response?.status === 400 && (code === 'BBOX_TOO_LARGE' || (typeof msg === 'string' && msg.toLowerCase().includes('zoom in')))) {
            onError?.('Zoom in to load forest roads.')
          }
        })
        .finally(() => {
          isLoadingRef.current = false
        })

      await Promise.all([quickFetch, fullFetch])
      return
    }

    isLoadingRef.current = true
    try {
      const response = await routesApiRef.current.getForestRoadsForBounds(fullBbox)
      onError?.(null)
      if (response && response.success && response.features) {
        setForestRoads(response.features)
      } else {
        setForestRoads([])
      }
    } catch (err: any) {
      setForestRoads([])
      const msg = err?.response?.data?.error
      const code = err?.response?.data?.code
      if (err?.response?.status === 400 && (code === 'BBOX_TOO_LARGE' || (typeof msg === 'string' && msg.toLowerCase().includes('zoom in')))) {
        onError?.('Zoom in to load forest roads.')
      }
    } finally {
      isLoadingRef.current = false
    }
  }, [clipBboxToMaxArea, onError])

  // Helper function to compare bounds (check if they're significantly different)
  const boundsChanged = useCallback((oldBounds: mapboxgl.LngLatBounds | null, newBounds: mapboxgl.LngLatBounds): boolean => {
    if (!oldBounds) {
      return true
    }

    const oldNE = oldBounds.getNorthEast()
    const oldSW = oldBounds.getSouthWest()
    const newNE = newBounds.getNorthEast()
    const newSW = newBounds.getSouthWest()

    // Consider bounds changed if any corner has moved by more than a threshold (e.g., 1% of the bounds size)
    const lngThreshold = Math.abs(oldNE.lng - oldSW.lng) * 0.01
    const latThreshold = Math.abs(oldNE.lat - oldSW.lat) * 0.01

    return (
      Math.abs(oldNE.lng - newNE.lng) > lngThreshold ||
      Math.abs(oldNE.lat - newNE.lat) > latThreshold ||
      Math.abs(oldSW.lng - newSW.lng) > lngThreshold ||
      Math.abs(oldSW.lat - newSW.lat) > latThreshold
    )
  }, [])

  // Debounced function to fetch forest roads when map moves
  const handleMapMove = useCallback(() => {
    if (!map.current || !isEnabled) {
      return
    }

    const bounds = map.current.getBounds()
    if (!bounds) {
      return
    }

    // Debounce to avoid too many API calls
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      // Only fetch if bounds have changed significantly
      if (boundsChanged(boundsRef.current, bounds)) {
        boundsRef.current = bounds
        fetchForestRoads(bounds)
      }
    }, 500) // 500ms debounce
  }, [isEnabled, fetchForestRoads, boundsChanged])

  // Render forest roads layer
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) {
      return
    }

    // Helper function to setup the layer
    const setupLayer = () => {
      if (!map.current) return

      if (!isEnabled) {
        initializedRef.current = false
        // Remove layer and source when disabled
        try {
          if (map.current.getLayer('forest-roads')) {
            map.current.removeLayer('forest-roads')
          }
          if (map.current.getSource('forest-roads')) {
            map.current.removeSource('forest-roads')
          }
          setForestRoads([])
          boundsRef.current = null
        } catch (error) {
          // Ignore errors if layer/source doesn't exist
        }
        // Remove event listeners
        if (eventListenersAttachedRef.current) {
          map.current.off('moveend', handleMapMove)
          map.current.off('zoomend', handleMapMove)
          eventListenersAttachedRef.current = false
        }
        return
      }

      // Prevent re-initialization if already initialized and enabled
      if (initializedRef.current && isEnabled) {
        return
      }

      initializedRef.current = true
      
      // Fetch forest roads for initial bounds (with progressive loading)
      const bounds = map.current.getBounds()
      if (bounds) {
        boundsRef.current = bounds
        fetchForestRoads(bounds, true) // true = isInitial, triggers progressive loading
      }

      // Listen to map move events to update forest roads (only if not already attached)
      if (!eventListenersAttachedRef.current) {
        map.current.on('moveend', handleMapMove)
        map.current.on('zoomend', handleMapMove)
        eventListenersAttachedRef.current = true
      }
    }

    // Trust styleLoaded from context - don't check isStyleLoaded() which can be false during animations
    // If context says style is loaded, proceed
    if (styleLoaded) {
      setupLayer()
    } else {
      // Wait for styleLoaded to become true - the effect will re-run
      return
    }

    return () => {
      if (map.current && eventListenersAttachedRef.current) {
        map.current.off('moveend', handleMapMove)
        map.current.off('zoomend', handleMapMove)
        eventListenersAttachedRef.current = false
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [map, isLoaded, styleLoaded, isEnabled, fetchForestRoads, handleMapMove])

  // Update layer when forest roads data changes
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded || !isEnabled) {
      return
    }

    // Trust styleLoaded from context - don't check isStyleLoaded() which can be false during animations
    if (!styleLoaded) {
      return
    }

    try {
      const hasSource = !!map.current.getSource('forest-roads')

      const geoJsonData = {
        type: 'FeatureCollection' as const,
        features: forestRoads
      }

      if (hasSource) {
        const source = map.current.getSource('forest-roads') as mapboxgl.GeoJSONSource
        source.setData(geoJsonData)
        // Update paint properties to ensure they're always red and thick
        if (map.current.getLayer('forest-roads')) {
          map.current.setPaintProperty('forest-roads', 'line-color', '#dc2626')
          map.current.setPaintProperty('forest-roads', 'line-width', 5)
          map.current.setPaintProperty('forest-roads', 'line-opacity', 0.8)
        }
      } else {
        // Create source
        map.current.addSource('forest-roads', {
          type: 'geojson',
          data: geoJsonData
        })

        // Create layer
        map.current.addLayer({
          id: 'forest-roads',
          type: 'line',
          source: 'forest-roads',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#dc2626', // Red color for forest roads
            'line-width': 5,
            'line-opacity': 0.8
          },
          minzoom: 10 // Only show at zoom 10 and above
        })

        // Ensure forest roads layer is below dead end markers but above base map
        const layersToMoveAbove = ['dead-end-markers', 'waypoint-markers', 'poi-markers']
        layersToMoveAbove.forEach(layerId => {
          if (map.current?.getLayer(layerId)) {
            try {
              map.current.moveLayer('forest-roads', layerId)
            } catch (e) {
              // Ignore if layer doesn't exist
            }
          }
        })
      }
    } catch (error) {
      // Silently handle errors - layer will retry on next update
    }
  }, [map, isLoaded, styleLoaded, isEnabled, forestRoads])
}

